import React, { useState, useEffect, useMemo, useRef } from 'react';
import VirtualTryOn from '../VirtualTryOn';
import { resolveLocale, getTranslations } from '../i18n/vto';

const waitForNextCatalogItem = () => new Promise((resolve) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => resolve(), { timeout: 120 });
    return;
  }

  window.setTimeout(resolve, 48);
});

/** Category filter for catalog: all | dioptric (prescription) | sunglasses */
const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'dioptric', label: 'Prescription' },
  { value: 'sunglasses', label: 'Sunglasses' },
];

/** Gender filter: all | male (men + unisex) | female (women + unisex) */
const GENDER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'male', label: 'Men' },
  { value: 'female', label: 'Women' },
];

/**
 * VTO Widget Component
 * Initializes the Virtual Try-On widget using the API
 * 
 * @param {string} apiKey - API key for authentication (Bearer token)
 * @param {string} sku - Product SKU to initialize
 * @param {string} apiBase - API base URL (default: https://api.vltryon.com)
 * @param {function} onError - Error callback
 * @param {function} onReady - Ready callback when config is loaded
 */
const VTOWidget = ({ 
  apiKey = process.env.REACT_APP_VTO_API_KEY || 'vl_cdfd382b1588473a9022051a4822e374',
  sku = process.env.REACT_APP_VTO_DEFAULT_SKU || null, // null = fetch full catalog
  apiBase = process.env.REACT_APP_VTO_API_BASE || 'https://api.vltryon.com',
  initialConfig = null,
  onError,
  onReady,
  embedded = false,
  minimalLoader = false
}) => {
  const [config, setConfig] = useState(initialConfig);
  const [loading, setLoading] = useState(!initialConfig);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all'); // all | male | female — only used when fetching catalog
  const [catalogLoadState, setCatalogLoadState] = useState({ loading: false, loaded: 0, total: 0 });
  const [selectedAssetReady, setSelectedAssetReady] = useState(!!initialConfig);
  const selectedAssetReadyRef = useRef(!!initialConfig);
  const selectedAssetReadyResolverRef = useRef(null);
  const [showFilterBarOnMobile, setShowFilterBarOnMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 640px)').matches;
  });

  const resetSelectedAssetReady = () => {
    selectedAssetReadyRef.current = false;
    selectedAssetReadyResolverRef.current = null;
    setSelectedAssetReady(false);
  };

  const markSelectedAssetReady = () => {
    if (selectedAssetReadyRef.current) return;
    selectedAssetReadyRef.current = true;
    setSelectedAssetReady(true);

    if (selectedAssetReadyResolverRef.current) {
      selectedAssetReadyResolverRef.current();
      selectedAssetReadyResolverRef.current = null;
    }
  };

  const waitForSelectedAssetReady = (timeoutMs = 3000) => new Promise((resolve) => {
    if (selectedAssetReadyRef.current) {
      resolve();
      return;
    }

    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      if (selectedAssetReadyResolverRef.current === finish) {
        selectedAssetReadyResolverRef.current = null;
      }
      resolve();
    }, timeoutMs);

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve();
    };

    selectedAssetReadyResolverRef.current = finish;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 640px)');
    const updateFilterBarVisibility = (event) => {
      setShowFilterBarOnMobile(event.matches);
    };

    setShowFilterBarOnMobile(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateFilterBarVisibility);
      return () => mediaQuery.removeEventListener('change', updateFilterBarVisibility);
    }

    mediaQuery.addListener(updateFilterBarVisibility);
    return () => mediaQuery.removeListener(updateFilterBarVisibility);
  }, []);

  // Initialize widget by calling the API (refetches when categoryFilter or genderFilter changes for catalog mode)
  useEffect(() => {
    let cancelled = false;

    const getProductKey = (product) => String(product?.id || product?.sku || '');

    const mergeSelectedFirst = (selectedProduct, products = []) => {
      const map = new Map();
      products.forEach((product) => {
        const key = getProductKey(product);
        if (key && !map.has(key)) map.set(key, product);
      });

      if (!selectedProduct) return Array.from(map.values());

      const selectedKey = getProductKey(selectedProduct);
      const rest = Array.from(map.entries())
        .filter(([key]) => key !== selectedKey)
        .map(([, product]) => product);
      return [selectedProduct, ...rest];
    };

    const appendProductsSequentially = async ({
      baseConfig,
      selectedProduct,
      products,
      initialProducts,
    }) => {
      const orderedProducts = mergeSelectedFirst(selectedProduct, products);
      const initialList = Array.isArray(initialProducts) ? initialProducts : [];
      const seen = new Set(initialList.map(getProductKey).filter(Boolean));
      const remainingProducts = orderedProducts.filter((product) => {
        const key = getProductKey(product);
        return key && !seen.has(key);
      });

      const total = orderedProducts.length;
      let loaded = initialList.length;

      if (!remainingProducts.length) {
        if (!cancelled) {
          setCatalogLoadState({ loading: false, loaded, total });
        }
        return orderedProducts;
      }

      if (!cancelled) {
        setCatalogLoadState({ loading: true, loaded, total });
      }

      for (const product of remainingProducts) {
        if (cancelled) break;

        loaded += 1;

        setConfig((prev) => ({
          ...(prev || baseConfig),
          products: [...((prev?.products && Array.isArray(prev.products)) ? prev.products : initialList), product],
        }));
        setCatalogLoadState({ loading: loaded < total, loaded, total });

        if (loaded < total) {
          await waitForNextCatalogItem();
        }
      }

      if (!cancelled) {
        setCatalogLoadState((prev) => ({
          ...prev,
          loading: false,
          loaded: Math.max(prev.loaded, loaded),
          total,
        }));
      }

      return orderedProducts;
    };

    if (initialConfig) {
      selectedAssetReadyRef.current = true;
      selectedAssetReadyResolverRef.current = null;
      setSelectedAssetReady(true);
      setConfig(initialConfig);
      setLoading(false);
      setError(null);
      setCatalogLoadState({ loading: false, loaded: 0, total: 0 });
      if (onReady) onReady(initialConfig);
      return () => {
        cancelled = true;
      };
    }

    const initWidget = async () => {
      if (!apiKey) {
        const err = { code: 'NO_API_KEY', message: 'API key is required' };
        setError(err);
        setLoading(false);
        if (onError) onError(err);
        return;
      }

      try {
        resetSelectedAssetReady();
        setLoading(true);
        setError(null);
        setCatalogLoadState({ loading: false, loaded: 0, total: 0 });

        // SKU mode: render selected product immediately, then stream remaining catalog.
        if (sku && sku !== 'YOUR_PRODUCT_SKU') {
          const catalogPromise = fetch(`${apiBase}/api/vto/catalog`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${apiKey}`,
              'Content-Type': 'application/json'
            }
          }).catch(() => null);

          const initResponse = await fetch(`${apiBase}/api/vto/init`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${apiKey}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ sku })
          });

          if (!initResponse.ok) {
            const errorData = await initResponse.json().catch(() => ({}));
            const err = {
              code: 'API_ERROR',
              status: initResponse.status,
              message: errorData.message || errorData.error || 'Failed to initialize widget'
            };
            setError(err);
            setLoading(false);
            if (onError) onError(err);
            return;
          }

          const initData = await initResponse.json();
          const selectedProduct = initData?.product || null;
          const fallbackInitProducts = Array.isArray(initData?.products) && initData.products.length > 0
            ? [initData.products[0]]
            : [];
          const initialProducts = selectedProduct ? [selectedProduct] : fallbackInitProducts;
          const initialData = {
            ...initData,
            products: initialProducts,
          };

          if (cancelled) return;
          setConfig(initialData);
          setLoading(false);
          setCatalogLoadState({
            loading: false,
            loaded: initialProducts.length,
            total: initialProducts.length,
          });
          if (onReady) onReady(initialData);

          const loadRemainingCatalog = async () => {
            try {
              const catalogResponse = await catalogPromise;
              if (!catalogResponse || !catalogResponse.ok) {
                console.warn('[VTOWidget] Failed to fetch full merchant catalog for compare mode:', catalogResponse?.status || 'network');
                return;
              }

              const catalogData = await catalogResponse.json().catch(() => null);
              await waitForSelectedAssetReady();
              if (cancelled) return;

              await appendProductsSequentially({
                baseConfig: initData,
                selectedProduct,
                products: Array.isArray(catalogData?.products) ? catalogData.products : initialProducts,
                initialProducts,
              });
            } catch (catalogErr) {
              console.warn('[VTOWidget] Background catalog load failed:', catalogErr);
            } finally {
              if (!cancelled) {
                setCatalogLoadState((prev) => ({ ...prev, loading: false }));
              }
            }
          };

          loadRemainingCatalog();
          return;
        } else {
          const params = new URLSearchParams();
          if (categoryFilter !== 'all') params.set('category', categoryFilter);
          if (genderFilter !== 'all') params.set('gender', genderFilter);
          const q = params.toString() ? `?${params.toString()}` : '';
          const response = await fetch(`${apiBase}/api/vto/catalog${q}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${apiKey}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = {
              code: 'API_ERROR',
              status: response.status,
              message: errorData.message || errorData.error || 'Failed to initialize widget'
            };
            setError(err);
            setLoading(false);
            if (onError) onError(err);
            return;
          }

          const data = await response.json();
          console.log('[VTOWidget] API response:', data);
          console.log('[VTOWidget] Products in response:', data.products?.length || (data.product ? 1 : 0));
          if (data.products) {
            console.log('[VTOWidget] Sample product:', data.products[0]);
          } else if (data.product) {
            console.log('[VTOWidget] Single product:', data.product);
          }
          const catalogProducts = Array.isArray(data.products) ? data.products : (data.product ? [data.product] : []);
          const initialProducts = catalogProducts.slice(0, 1);
          if (cancelled) return;
          setConfig({
            ...data,
            products: initialProducts,
          });
          setCatalogLoadState({
            loading: catalogProducts.length > initialProducts.length,
            loaded: initialProducts.length,
            total: catalogProducts.length,
          });
          setLoading(false);
          if (onReady) {
            onReady({
              ...data,
              products: catalogProducts,
            });
          }

          await waitForSelectedAssetReady();
          if (cancelled) return;

          await appendProductsSequentially({
            baseConfig: data,
            selectedProduct: data.product || initialProducts[0] || null,
            products: catalogProducts,
            initialProducts,
          });
        }
      } catch (err) {
        const errorObj = {
          code: 'NETWORK_ERROR',
          message: err.message || 'Network error'
        };
        setError(errorObj);
        setLoading(false);
        if (onError) onError(errorObj);
      }
    };

    initWidget();

    return () => {
      cancelled = true;
    };
  }, [apiKey, sku, apiBase, categoryFilter, genderFilter, initialConfig, onError, onReady]);

  // Build custom catalog from API config
  const customCatalog = useMemo(() => {
    if (!config) {
      console.log('[VTOWidget] No config available');
      return null;
    }

    // Handle both single product (from /init) and catalog (from /catalog) responses
    let products = [];
    if (config.products && Array.isArray(config.products)) {
      // Full catalog response
      products = config.products;
    } else if (config.product) {
      // Single product response
      products = [config.product];
    } else {
      console.log('[VTOWidget] No products in config');
      return null;
    }

    if (products.length === 0) {
      console.log('[VTOWidget] Empty products array');
      return null;
    }
    
    // Convert API products to VirtualTryOn catalog format
    // VirtualTryOn expects: src (front image), handleSrc (handle image), modelUrl (3D), assetType
    const catalogItems = products
      .filter((product) => {
        // EXACT same filter as ProductsPage VTOPreviewModal line 1087:
        // p.active && (p.frontImageUrl || (p.assetType === '3d' && p.modelUrl))
        const isActive = product.active !== false; // Default to true if not specified
        const has2DImage = !!product.frontImageUrl; // Only need frontImageUrl (not handleImageUrl required)
        const has3DModel = product.assetType === '3d' && !!product.modelUrl;
        const isValid = isActive && (has2DImage || has3DModel);
        
        if (!isValid) {
          console.warn('[VTOWidget] Skipping product:', product.sku || product.id, {
            active: isActive,
            assetType: product.assetType,
            hasFrontImage: !!product.frontImageUrl,
            hasHandleImage: !!product.handleImageUrl,
            hasModel: !!product.modelUrl
          });
        }
        
        return isValid;
      })
      .map((product) => {
        // Use EXACT same format as ProductsPage VTOPreviewModal (lines 1086-1102)
        // Note: handleSrc can be null/undefined - VirtualTryOn will use default handle
        return {
          id: product.id || product.sku, // ProductsPage uses p.id
          name: product.name || product.sku,
          src: product.frontImageUrl, // ProductsPage uses p.frontImageUrl (required for 2D)
          handleSrc: product.handleImageUrl || null, // ProductsPage uses p.handleImageUrl (optional - can be null)
          category: product.category || 'dioptric',
          gender: product.gender || 'unisex',
          addToCartUrl: product.addToCartUrl || null, // ProductsPage uses p.addToCartUrl || null
          frameWidth: product.frameWidth || 140, // ProductsPage uses p.frameWidth || 140
          handleWidth: product.handleWidth || 140, // ProductsPage uses p.handleWidth || 140
          bridgeLengthCm: product.bridgeLengthCm ?? 1.8, // Bridge length in cm
          frontContentScaleX: product.frontContentScaleX ?? 1,
          frontContentScaleY: product.frontContentScaleY ?? 1,
          frontContentBBox: product.frontContentBBox ?? null,
          handleContentScaleX: product.handleContentScaleX ?? 1,
          handleContentScaleY: product.handleContentScaleY ?? 1,
          handleContentBBox: product.handleContentBBox ?? null,
          scaleCorrection: product.scaleCorrection ?? 1,
          templeHingeOffset: product.templeHingeOffset ?? product.templeConfig?.hingeOffset ?? 0.95, // ProductsPage uses p.templeHingeOffset ?? 0.95
          templeYOffset: product.templeYOffset ?? product.templeConfig?.yOffset ?? 0.02, // ProductsPage uses p.templeYOffset ?? 0.02
          templeLength: product.templeLength ?? product.templeConfig?.length ?? 3, // ProductsPage uses p.templeLength ?? 3
          recommendedForFaceShapes: product.recommendedFaceShapes || [], // ProductsPage uses p.recommendedFaceShapes || []
          // 3D model support
          assetType: product.assetType || '2d', // ProductsPage uses p.assetType || '2d'
          modelUrl: product.modelUrl || null, // ProductsPage uses p.modelUrl || null
          // Clip-on attachment
          clipOnSrc: product.clipOnImageUrl || null,
          clipOnHingeOffset: product.clipOnHingeOffset ?? 0.95,
          clipOnYOffset: product.clipOnYOffset ?? 0.02,
        };
      });

    if (config.product) {
      const selectedProductId = config.product.id || config.product.sku;
      const selectedIndex = catalogItems.findIndex((item) => item.id === selectedProductId);
      if (selectedIndex > 0) {
        const [selected] = catalogItems.splice(selectedIndex, 1);
        catalogItems.unshift(selected);
      }
    }
    
    console.log('[VTOWidget] Built catalog with', catalogItems.length, 'items (filtered from', products.length, 'total)');
    catalogItems.forEach((item, index) => {
      console.log(`[VTOWidget] Item ${index + 1}:`, {
        id: item.id,
        name: item.name,
        assetType: item.assetType,
        src: item.src ? (item.src.length > 60 ? item.src.substring(0, 60) + '...' : item.src) : 'MISSING',
        handleSrc: item.handleSrc ? (item.handleSrc.length > 60 ? item.handleSrc.substring(0, 60) + '...' : item.handleSrc) : 'MISSING',
        modelUrl: item.modelUrl ? (item.modelUrl.length > 60 ? item.modelUrl.substring(0, 60) + '...' : item.modelUrl) : 'N/A'
      });
    });
    
    if (catalogItems.length === 0) {
      console.error('[VTOWidget] No valid products after filtering. Original products:', products);
    }
    
    return catalogItems;
  }, [config]);

  const { locale, t } = useMemo(() => {
    const widgetSettings = config?.widgetSettings;
    const resolved = resolveLocale(widgetSettings || {});
    return { locale: resolved, t: getTranslations(resolved) };
  }, [config?.widgetSettings]);

  // Loading state
  if (loading) {
    if (minimalLoader) {
      return (
        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-blue-500/80 border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">{t('loadingVTO')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">{t('failedToLoad')}</h3>
          <p className="text-white/60 mb-4">{error.message}</p>
        </div>
      </div>
    );
  }

  // Render VirtualTryOn with the config from API
  if (!config || !customCatalog || customCatalog.length === 0) {
    console.log('[VTOWidget] Not rendering - config:', !!config, 'catalog:', customCatalog);
    return null;
  }

  console.log('[VTOWidget] Rendering VirtualTryOn with catalog:', customCatalog);
  console.log('[VTOWidget] Catalog length:', customCatalog?.length);
  console.log('[VTOWidget] Show catalog:', customCatalog && customCatalog.length > 1);
  console.log('[VTOWidget] Embedded mode:', embedded);

  const isCatalogMode = !sku || sku === 'YOUR_PRODUCT_SKU';
  const apiProductCount = Array.isArray(config?.products)
    ? config.products.length
    : (config?.product ? 1 : (customCatalog?.length || 0));
  const hasSingleProduct = apiProductCount <= 1;
  const widgetSettings = config?.widgetSettings || {};
  const showCategoryFilterBar = isCatalogMode && !hasSingleProduct && customCatalog?.length > 0 && (widgetSettings.showCategoryFilter !== false);
  const showGenderFilterBar = isCatalogMode && !hasSingleProduct && customCatalog?.length > 0 && (widgetSettings.showGenderFilter !== false);
  const hasAnyFilterBar = showCategoryFilterBar || showGenderFilterBar;

  return (
    <div className="w-full h-full flex flex-col md:p-3">
      {hasAnyFilterBar && showFilterBarOnMobile && (
        <div className="flex flex-wrap items-center gap-3 p-2 bg-black/30 border-b border-white/10 flex-shrink-0">
          {showCategoryFilterBar && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-white/70 mr-1">Category:</span>
              {CATEGORY_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCategoryFilter(value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    categoryFilter === value
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white border border-white/10'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {showGenderFilterBar && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-white/70 mr-1">Gender:</span>
              {GENDER_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setGenderFilter(value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    genderFilter === value
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white border border-white/10'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="relative flex-1 min-h-0 md:rounded-2xl md:overflow-hidden">
        {catalogLoadState.loading && catalogLoadState.total > catalogLoadState.loaded && (
          <div className="absolute z-20 right-3 top-3 bg-black/55 border border-white/15 rounded-full px-3 py-1.5 text-xs text-white/85 backdrop-blur-sm">
            Loading models {Math.min(catalogLoadState.loaded, catalogLoadState.total)}/{catalogLoadState.total}
          </div>
        )}
        <VirtualTryOn 
          catalog={customCatalog} 
          embedded={embedded}
          showCatalog={(catalogLoadState.total || customCatalog?.length || 0) > 1}
          initialSelectedGlassesId={config?.product?.id || config?.product?.sku || customCatalog?.[0]?.id || null}
          locale={locale}
          translations={t}
          enableCatalogPrefetch={selectedAssetReady}
          onSelectedGlassesLoaded={markSelectedAssetReady}
        />
      </div>
    </div>
  );
};

export default VTOWidget;
